import { Injectable, signal } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SaveWorksheetDto, SheetDataDto } from 'src/app/model/saveWorksheetDto';
import { ApplicationContext, SourceSheet, getTableConfig } from 'src/app/model';
import {
  SAVE_WORKSHEET_SETTINGS,
  WOKRPAPER_SHEETS,
  WORKBOOK_ERROR_MESSAGES,
  WORKPAPER_OPERATION_KIND,
  WORKPAPER_OPERATION_LABELS,
  WORKPAPER_OPERATION_MESSAGES,
} from 'src/app/constants/workbook.constants';
import { ExcelHelperService } from '../addin-helper-services/excel-helper.service';
import { GlobalStateService } from '../global-state/global-state.service';
import { ApplicationInsightsService } from '../application-insights/application-insights.service';
import { ReconDataService } from '../output-data/recon-data.service';
import { ReturnsCalendarDataService } from '../output-data/returns-calendar-data.service';
import { CreditCarryForwardDataService } from '../output-data/credit-carry-forward-data.service';
import { TaxColNotRegisteredDataService } from '../output-data/tax-col-not-registered-data.service';
import { WorkpaperOperationLockService } from '../workpaper-operation-lock/workpaper-operation-lock.service';

@Injectable({ providedIn: 'root' })
export class SaveWorksheetService {
  private readonly url = `${environment.apiBaseURL}/api/Workpaper/SaveWorksheetToDatabase`;

  private readonly saveSheetConfigs = {
    recon: getTableConfig(SourceSheet.Recon),
    returnsCalendar: getTableConfig(SourceSheet.ReturnsCalendar),
    creditCarryForward: getTableConfig(SourceSheet.CreditCarryForward),
    taxColNotRegistered: getTableConfig(SourceSheet.TaxColNotRegistered),
  };

  private readonly guidPattern =
    /^[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}$/;

  // Reactive state — read directly by HeaderComponent via injection.
  readonly isSaving = signal(false);
  readonly saveMessage = signal('');
  readonly saveMessageType = signal<'success' | 'error' | ''>('');

  constructor(
    private readonly http: HttpClient,
    private readonly excelHelperService: ExcelHelperService,
    private readonly globalStateService: GlobalStateService,
    private readonly appInsights: ApplicationInsightsService,
    private readonly reconDataService: ReconDataService,
    private readonly returnsCalendarDataService: ReturnsCalendarDataService,
    private readonly creditCarryForwardDataService: CreditCarryForwardDataService,
    private readonly taxColNotRegisteredDataService: TaxColNotRegisteredDataService,
    private readonly operationLockService: WorkpaperOperationLockService,
  ) {}

  /** HTTP call — kept as an Observable for callers that need composability. */
  saveWorksheetToDatabase(dto: SaveWorksheetDto): Observable<any> {
    return this.http.post<any>(this.url, dto, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /** Clears the save feedback message. Call when navigating away. */
  clearMessage(): void {
    this.saveMessage.set('');
    this.saveMessageType.set('');
  }

  async save(): Promise<void> {
    this.appInsights.trackEvent('SaveWorksheetService.save.called');

    try {
      await this.operationLockService.runExclusive(
        { kind: WORKPAPER_OPERATION_KIND.Save, label: WORKPAPER_OPERATION_LABELS.Save },
        () => this.saveInternal()
      );
    } catch (error) {
      const lockError = error instanceof Error ? error : new Error(String(error));
      this.appInsights.trackException(lockError, { location: 'SaveWorksheetService.save.lock' });
      this.setMessage(WORKPAPER_OPERATION_MESSAGES.SaveBlocked, 'error');
    }
  }

  private async saveInternal(): Promise<void> {
    this.clearMessage();
    this.isSaving.set(true);
    const startTime = performance.now();

    try {
      const appContext: ApplicationContext = this.globalStateService.getWorkpaperContextData();

      this.appInsights.trackEvent('SaveWorksheetService.save.readingSheets');
      const [reconSheetData, returnCalendarSheetData, creditCarryForwardSheetData, taxColNotRegisteredSheetData] =
        await Promise.all([
          this.readSheetPayload('recon', this.saveSheetConfigs.recon.sheetName, this.saveSheetConfigs.recon.tableName, this.reconDataService.getSaveHeaders()),
          this.readSheetPayload('returns-calendar', this.saveSheetConfigs.returnsCalendar.sheetName, this.saveSheetConfigs.returnsCalendar.tableName, this.returnsCalendarDataService.getSaveHeaders()),
          this.readSheetPayload('credit-carry-forward', this.saveSheetConfigs.creditCarryForward.sheetName, this.saveSheetConfigs.creditCarryForward.tableName, this.creditCarryForwardDataService.getSaveHeaders()),
          this.readSheetPayload('tax-col-not-registered', this.saveSheetConfigs.taxColNotRegistered.sheetName, this.saveSheetConfigs.taxColNotRegistered.tableName, this.taxColNotRegisteredDataService.getSaveHeaders()),
        ]);
      this.appInsights.trackEvent('SaveWorksheetService.save.sheetsRead');

      const adminValues = [
        appContext.clientId,
        appContext.entityId || '',
        appContext.clientTaxPeriodId,
        appContext.reportingGroupId || '',
      ];

      const clientIdRaw = (adminValues[0] ?? '').toString().trim();
      const entityIdRaw = (adminValues[1] ?? '').toString().trim();
      const clientTaxPeriodIdRaw = (adminValues[2] ?? '').toString().trim();
      const reportingGroupIdRaw = (adminValues[3] ?? '').toString().trim();

      const hasEntityId = entityIdRaw.length > 0;
      const hasReportingGroupId = reportingGroupIdRaw.length > 0;

      if (!this.guidPattern.test(clientIdRaw)) {
        this.setMessage('Cannot save worksheet: invalid ClientId in Admin context.', 'error');
        return;
      }

      if (!Number.isInteger(Number(clientTaxPeriodIdRaw)) || Number(clientTaxPeriodIdRaw) <= 0) {
        this.setMessage('Cannot save worksheet: invalid ClientTaxPeriodId in Admin context.', 'error');
        return;
      }

      if (hasEntityId && hasReportingGroupId) {
        this.setMessage('Cannot save worksheet: both EntityId and ReportingGroupId are populated in Admin context.', 'error');
        return;
      }

      if (!hasEntityId && !hasReportingGroupId) {
        this.setMessage('Cannot save worksheet: neither EntityId nor ReportingGroupId is populated in Admin context.', 'error');
        return;
      }

      if (hasEntityId && !this.guidPattern.test(entityIdRaw)) {
        this.setMessage('Cannot save worksheet: invalid EntityId in Admin context.', 'error');
        return;
      }

      this.appInsights.trackEvent('SaveWorksheetService.save.adminValuesFetched', { adminValues });

      const reconSheetDataProcessed = this.reconDataService.prepareReconSheetData(reconSheetData);
      const returnCalendarSheetDataProcessed = this.returnsCalendarDataService.prepareReturnCalendarSheetData(returnCalendarSheetData);
      const creditCarryForwardSheetDataProcessed = this.creditCarryForwardDataService.prepareCreditCarryForwardSheetData(creditCarryForwardSheetData);
      const taxColNotRegisteredSheetDataProcessed = this.taxColNotRegisteredDataService.prepareTaxColNotRegisteredSheetData(taxColNotRegisteredSheetData);

      const sheets: SheetDataDto[] = [
        { sheetName: WOKRPAPER_SHEETS.ReconSheet, sheetData: reconSheetDataProcessed.rows },
        { sheetName: WOKRPAPER_SHEETS.ReturnsCalendarSheet, sheetData: returnCalendarSheetDataProcessed.rows },
      ];

      if (creditCarryForwardSheetDataProcessed.rows.length > 1) {
        sheets.push({ sheetName: WOKRPAPER_SHEETS.CreditCarryForwardSheet, sheetData: creditCarryForwardSheetDataProcessed.rows });
      }

      if (taxColNotRegisteredSheetDataProcessed.rows.length > 1) {
        sheets.push({ sheetName: WOKRPAPER_SHEETS.TaxColNotregisteredSheet, sheetData: taxColNotRegisteredSheetDataProcessed.rows });
      }

      const requestDto: SaveWorksheetDto = { sheets, adminSheetValues: adminValues };

      const payloadAnalysis = this.analyzePayload(requestDto);
      this.appInsights.trackEvent('SaveWorksheetService.save.payloadAnalysis', {
        payloadBytes: payloadAnalysis.payloadBytes,
        payloadLimitBytes: SAVE_WORKSHEET_SETTINGS.MAX_PAYLOAD_BYTES,
        sheetPayloadRows: payloadAnalysis.sheetPayloadRows,
        isPayloadWithinLimit: payloadAnalysis.payloadBytes <= SAVE_WORKSHEET_SETTINGS.MAX_PAYLOAD_BYTES,
      });

      if (payloadAnalysis.payloadBytes > SAVE_WORKSHEET_SETTINGS.MAX_PAYLOAD_BYTES) {
        this.setMessage('Cannot save worksheet: payload is too large for Excel Online. Reduce row count and try again.', 'error');
        this.appInsights.trackEvent('SaveWorksheetService.save.payloadTooLarge', {
          payloadBytes: payloadAnalysis.payloadBytes,
          payloadLimitBytes: SAVE_WORKSHEET_SETTINGS.MAX_PAYLOAD_BYTES,
          sheetPayloadRows: payloadAnalysis.sheetPayloadRows,
        });
        return;
      }

      this.appInsights.trackEvent('SaveWorksheetService.save.savingWorksheet');

      // firstValueFrom converts the single-shot HTTP observable to a promise —
      // no manual Subscription / teardown needed in a root-scoped service.
      try {
        const response = await firstValueFrom(this.saveWorksheetToDatabase(requestDto));
        this.appInsights.trackEvent('SaveWorksheetService.save.success', { response });
        this.setMessage(
          response.Value && response.Success
            ? 'Worksheet saved successfully! '
            : 'An error occured while saving the worksheet: ' + response.Value,
          response.Success ? 'success' : 'error',
        );
        console.log('Worksheet saved successfully', response);
      } catch (httpErr: any) {
        this.appInsights.trackException(httpErr, { location: 'SaveWorksheetService.save.http' });
        this.setMessage('Failed to save worksheet: ' + (httpErr?.message || WORKBOOK_ERROR_MESSAGES.UnknownError), 'error');
        console.error('Save failed', httpErr);
      }

    } catch (err: any) {
      this.appInsights.trackException(err, { location: 'SaveWorksheetService.save' });
      this.appInsights.trackEvent('SaveWorksheetService.save.error', { error: err?.message || err });
      this.setMessage('Failed to save worksheet: ' + (err?.message || WORKBOOK_ERROR_MESSAGES.UnknownError), 'error');
      console.error('Error reading sheet data:', err);
    } finally {
      this.appInsights.trackEvent('SaveWorksheetService.save.completed', { durationMs: performance.now() - startTime });
      this.isSaving.set(false);
    }
  }

  private setMessage(message: string, messageType: 'success' | 'error' | ''): void {
    this.saveMessage.set(message);
    this.saveMessageType.set(messageType);
  }

  private analyzePayload(requestDto: SaveWorksheetDto): { payloadBytes: number; sheetPayloadRows: string } {
    const payloadJson = JSON.stringify(requestDto);
    const payloadBytes = new Blob([payloadJson]).size;
    const sheetPayloadRows = requestDto.sheets
      .map((sheet) => `${sheet.sheetName}:${Math.max(sheet.sheetData.length - 1, 0)}`)
      .join('|');
    return { payloadBytes, sheetPayloadRows };
  }

  private async readSheetPayload(
    logicalSheetName: string,
    sheetName: string,
    tableName: string,
    headers: string[],
  ): Promise<any[][]> {
    const readStart = performance.now();
    const matrix = await this.excelHelperService.getTableValuesByHeaders(sheetName, tableName, headers);
    const readDurationMs = Math.round(performance.now() - readStart);
    this.appInsights.trackEvent('SaveWorksheetService.save.sheetReadComplete', {
      logicalSheetName,
      rowCount: matrix.length,
      columnCount: matrix[0]?.length ?? 0,
      durationMs: readDurationMs,
    });
    return matrix;
  }
}

